import { z } from 'zod';

export const UserRole = {
  user: 'USER',
  admin: 'ADMIN',
  superadmin: 'SUPERADMIN',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export const InvitationCreateSchema = z
  .object({
    email: z.string().email('Enter a valid e-mail'),
    role: z.enum([UserRole.user, UserRole.admin, UserRole.superadmin]),
    clinicId: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    note: z.string().optional(),
    expiresInDays: z.coerce.number().int().min(1).max(90),
  })
  // A clinic user with no clinic would sign in to an empty app; the backend rejects it too.
  .refine((data) => data.role !== UserRole.user || Boolean(data.clinicId), {
    message: 'Pick a clinic for a clinic user',
    path: ['clinicId'],
  });

export type InvitationCreate = z.infer<typeof InvitationCreateSchema>;

export type InvitationResponse = {
  id: string;
  email: string;
  role: UserRole;
  status: InvitationStatus;
  clinicId?: string | null;
  clinicName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  note?: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
  revokedAt?: string | null;
  invitedByEmail?: string | null;
  /** Copied out of the panel by hand — there is no mailer yet. */
  inviteUrl: string;
};

export type InvitationPreview = {
  email: string;
  role: UserRole;
  clinicName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  expiresAt: string;
};

export const InvitationAcceptSchema = z
  .object({
    username: z.string().min(3, 'At least 3 characters'),
    password: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type InvitationAcceptForm = z.infer<typeof InvitationAcceptSchema>;

export type InvitationAccept = Omit<InvitationAcceptForm, 'confirmPassword'>;
